/**
 * Minimal cross-component bus for "wallet balance probably changed".
 *
 * Why: `WalletPill.tsx` is a self-contained header HUD that polls
 * every 60s + on window focus. Without a push channel, any in-app
 * action that mutates the wallet (activity claim, skin acquire,
 * topup return) leaves the pill stale until the next tick or until
 * the user manually refreshes.
 *
 * This is intentionally NOT react-query — WalletPill predates the
 * query cache and refactoring it would ripple. A `window` CustomEvent
 * is the smallest possible coupling: zero new dependencies, no
 * provider plumbing, works across portals + modal stacks.
 */

const EVENT_NAME = 'embedFanWalletChanged';

/**
 * Fire-and-forget — call this RIGHT AFTER any action you know
 * credits / debits the fan's platform-points wallet. Subscribers
 * will re-fetch on their next microtask.
 */
export function notifyWalletChanged(): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent(EVENT_NAME));
  } catch {
    // Old browsers without CustomEvent constructor — fall back to
    // legacy createEvent.
    try {
      const ev = document.createEvent('Event');
      ev.initEvent(EVENT_NAME, false, false);
      window.dispatchEvent(ev);
    } catch { /* ignore — HUD will catch up on next 60s tick */ }
  }
}

/**
 * Subscribe a callback. Returns a cleanup function suitable for a
 * React useEffect return value.
 */
export function subscribeWalletChanged(fn: () => void): () => void {
  if (typeof window === 'undefined') return () => { /* no-op */ };
  window.addEventListener(EVENT_NAME, fn);
  return () => window.removeEventListener(EVENT_NAME, fn);
}
